/**
 * Today: where an Agora opens (agora#215). Not a fifth inbox -- one list of
 * what wants you, read straight off Mail's, Calendar's and Chat's own SDKs
 * (there is no Activity feed service, and none is coming in this slice),
 * plus the day's own agenda.
 *
 * Matches the design canvas's `Today.dc.html` for two of its three parts.
 * The third, "Around right now", needs presence, which `@wtfalch/chat` does
 * not have yet (https://github.com/wtfalch/chat/issues/7) -- it is left out
 * entirely rather than drawn empty or faked. The canvas's missed-call row is
 * left out for the same reason: calls are not wired into this app.
 *
 * Each source loads on its own effect and fails on its own: a mailbox this
 * account cannot reach still lets a calendar invite or a chat mention show
 * up, and says so in a quiet line rather than losing the whole card.
 */
import type { CalendarClient, CalendarEvent } from '@wtfalch/calendar/calendar';
import { CalendarError } from '@wtfalch/calendar/calendar';
import type { ChatClient } from '@wtfalch/chat';
import { Button, Empty, Icon, Presence, Select } from '@wtfalch/design';
import type { MailClient } from '@wtfalch/email/mailbox';
import {
  identities as fetchIdentities,
  findRole,
  mailboxes,
  queryThreads,
  toMailError,
} from '@wtfalch/email/mailbox';
import { useEffect, useMemo, useState } from 'react';
import { presenceState } from '../chat/presence.tsx';
import { pathForCalendar, pathForMail, pathForMailCompose } from '../routes.ts';
import { Avatar, stickerOf } from '../ui/Avatar.tsx';
import type { AgendaRow, AroundPerson, WaitingRow } from './rows.ts';
import {
  actionIcon,
  agendaRows,
  aroundRightNow,
  calendarWaitingRows,
  chatWaitingRows,
  greeting,
  isToday,
  mailWaitingRows,
  orderWaitingRows,
  peopleLine,
  SOURCE_HUE,
  todayWindowRange,
  whenLabel,
} from './rows.ts';
import './today.css';

type SourceFilter = 'all' | WaitingRow['source'];

const FILTER_OPTIONS: readonly { value: SourceFilter; label: string }[] = [
  { value: 'all', label: 'Everything' },
  { value: 'mail', label: 'Mail' },
  { value: 'calendar', label: 'Calendar' },
  { value: 'chat', label: 'Chat' },
];

export function Today({
  agoraId,
  mailClient,
  calendarClient,
  chatClient,
  callsEnabled = false,
  go,
}: {
  agoraId: string;
  mailClient: MailClient | null;
  calendarClient: CalendarClient | null;
  chatClient: ChatClient | null;
  /** Calls are on for this agora, so a meeting under way offers Join. */
  callsEnabled?: boolean;
  go: (path: string) => void;
}) {
  const [mailRows, setMailRows] = useState<readonly WaitingRow[] | null>(null);
  const [mailError, setMailError] = useState<string | null>(null);
  const [account, setAccount] = useState<{ accountName: string; username: string }>({
    accountName: '',
    username: '',
  });
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');

  const [calendarRows, setCalendarRows] = useState<readonly WaitingRow[] | null>(null);
  const [agenda, setAgenda] = useState<readonly CalendarEvent[] | null>(null);
  // Each calendar's own colour, so an event draws the same bar here as it does
  // in the week.
  const [calendarColors, setCalendarColors] = useState<ReadonlyMap<string, string>>(new Map());
  const [myAddress, setMyAddress] = useState('');
  const [calendarError, setCalendarError] = useState<string | null>(null);

  const [answering, setAnswering] = useState<ReadonlySet<string>>(new Set());
  // Keyed by invitation id, so a failure on one invitation is not wiped out
  // by an unrelated answer -- success or failure -- on another (agora#496).
  const [answerErrors, setAnswerErrors] = useState<ReadonlyMap<string, string>>(new Map());

  const [chatRows, setChatRows] = useState<readonly WaitingRow[] | null>(null);
  const [around, setAround] = useState<readonly AroundPerson[]>([]);
  const [chatError, setChatError] = useState<string | null>(null);

  // Who to greet: the mail SDK's own identities carry the real name --
  // the same field the composer's signature line reads -- else the
  // address; best-effort, so a refusal here still lets everything else
  // load, and the greeting just falls back to "there".
  useEffect(() => {
    if (!mailClient) {
      setAccount({ accountName: '', username: '' });
      return;
    }
    let live = true;
    fetchIdentities(mailClient)
      .then((ids) => {
        if (!live) return;
        const identity = ids[0];
        setAccount(
          identity
            ? { accountName: identity.name, username: identity.email }
            : { accountName: '', username: '' },
        );
      })
      .catch(() => {
        // The mail effect below reports this failure; the greeting just
        // falls back to "there".
      });
    return () => {
      live = false;
    };
  }, [mailClient]);

  // Mail: unread threads in the inbox sent to this account.
  useEffect(() => {
    if (!mailClient) {
      setMailRows([]);
      setMailError(null);
      return;
    }
    let live = true;
    setMailError(null);
    (async () => {
      const tree = await mailboxes(mailClient);
      const inbox = findRole(tree, 'inbox');
      const ids = await fetchIdentities(mailClient);
      const me = ids[0]?.email ?? '';
      if (!inbox) {
        if (live) setMailRows([]);
        return;
      }
      const page = await queryThreads(
        mailClient,
        { inMailbox: inbox.id, notKeyword: '$seen' },
        { sort: [{ property: 'receivedAt', isAscending: false }], limit: 20 },
      );
      if (live) setMailRows(mailWaitingRows(page.items, me, agoraId, inbox.id));
    })().catch((err: unknown) => {
      if (!live) return;
      setMailError(toMailError(err).message);
      setMailRows([]);
    });
    return () => {
      live = false;
    };
  }, [mailClient, agoraId]);

  // Calendar: today's events, and any invitation still needing an answer
  // over the next 30 days -- one fetch serves both (`todayWindowRange`).
  useEffect(() => {
    if (!calendarClient) {
      setCalendarRows([]);
      setAgenda([]);
      setCalendarError(null);
      return;
    }
    let live = true;
    setCalendarError(null);
    const now = new Date();
    (async () => {
      const identity = await calendarClient.identity();
      const events = await calendarClient.listEvents(todayWindowRange(now));
      // Colours are decoration: a calendar list that fails leaves the grey bar.
      const calendars = await calendarClient.listCalendars().catch(() => []);
      if (!live) return;
      setCalendarColors(
        new Map(calendars.flatMap((c) => (c.color ? ([[c.id, c.color]] as const) : []))),
      );
      setMyAddress(identity.calendarAddress);
      setCalendarRows(calendarWaitingRows(events, identity.calendarAddress, agoraId));
      setAgenda(events);
    })().catch((err: unknown) => {
      if (!live) return;
      setCalendarError(
        err instanceof CalendarError ? err.message : 'Something went wrong reaching the calendar.',
      );
      setCalendarRows([]);
      setAgenda([]);
    });
    return () => {
      live = false;
    };
  }, [calendarClient, agoraId]);

  // Chat: rooms with anything unread, scanned for an unread mention. Reading
  // a mention needs that room's timeline, so this is limited to rooms
  // `unreadCount` already says have something unread, rather than paging
  // every room's history to look.
  useEffect(() => {
    if (!chatClient) {
      setChatRows([]);
      setAround([]);
      setChatError(null);
      return;
    }
    let live = true;
    let unwatch: (() => void) | undefined;
    setChatError(null);
    (async () => {
      await chatClient.start();
      if (!live) return;
      // Presence is decoration: when reading it fails, the card goes and
      // the waiting chat rows below still load.
      const readAround = () => {
        try {
          return aroundRightNow(
            chatClient.rooms(),
            (roomId) => chatClient.roomMembers(roomId),
            (userId) => chatClient.presence(userId),
            chatClient.userId() ?? '',
            agoraId,
          );
        } catch {
          return [];
        }
      };
      setAround(readAround());
      unwatch = chatClient.onPresenceChanged(() => {
        if (live) setAround(readAround());
      });
      const entries = chatClient
        .rooms()
        .filter((room) => room.unreadCount > 0)
        .map((room) => ({
          room,
          events: chatClient.timeline(room.id).events,
          readUpToEventId: chatClient.readUpToEventId(room.id),
        }));
      setChatRows(chatWaitingRows(entries, agoraId));
    })().catch(() => {
      if (!live) return;
      setChatError('Chat could not be reached.');
      setChatRows([]);
    });
    return () => {
      live = false;
      unwatch?.();
    };
  }, [chatClient, agoraId]);

  const clearAnswerError = (rowId: string) =>
    setAnswerErrors((prev) => {
      if (!prev.has(rowId)) return prev;
      const next = new Map(prev);
      next.delete(rowId);
      return next;
    });

  const answer = (row: WaitingRow, status: 'accepted' | 'declined') => {
    if (!calendarClient || !row.invite || answering.has(row.id)) return;
    clearAnswerError(row.id);
    setAnswering((prev) => new Set(prev).add(row.id));
    calendarClient
      .respond(row.invite.eventId, status, { sendSchedulingMessages: true })
      .then(() => {
        setCalendarRows((prev) => (prev ?? []).filter((r) => r.id !== row.id));
        // Clearing only at the start of the next attempt (above) leaves a
        // stale message up if an earlier, slower answer for this same row
        // resolves after a later success -- clear it here too. Keyed by
        // invitation id, this can never shadow a *different* invitation's
        // error (agora#496).
        clearAnswerError(row.id);
      })
      .catch((err: unknown) => {
        const message =
          err instanceof CalendarError ? err.message : 'Your answer could not be sent.';
        setAnswerErrors((prev) => {
          const next = new Map(prev);
          next.set(row.id, message);
          return next;
        });
      })
      .finally(() =>
        setAnswering((prev) => {
          const next = new Set(prev);
          next.delete(row.id);
          return next;
        }),
      );
  };

  const loaded = mailRows !== null && calendarRows !== null && chatRows !== null;
  const rows = useMemo(
    () => orderWaitingRows([...(mailRows ?? []), ...(calendarRows ?? []), ...(chatRows ?? [])]),
    [mailRows, calendarRows, chatRows],
  );
  const filteredRows = useMemo(
    () => (sourceFilter === 'all' ? rows : rows.filter((r) => r.source === sourceFilter)),
    [rows, sourceFilter],
  );
  const todaysEvents = useMemo(() => {
    const now = new Date();
    return (agenda ?? []).filter((e) => isToday(e, now));
  }, [agenda]);
  const items = useMemo(
    () => agendaRows(agenda ?? [], new Date(), agoraId, calendarColors, myAddress),
    [agenda, agoraId, calendarColors, myAddress],
  );
  const board = useMemo(
    () => greeting(new Date(), account.accountName, account.username, rows.length, todaysEvents),
    [account, rows.length, todaysEvents],
  );
  const errors = [
    mailError && { key: 'mail', message: `Mail: ${mailError}` },
    calendarError && { key: 'calendar', message: `Calendar: ${calendarError}` },
    chatError && { key: 'chat', message: `Chat: ${chatError}` },
    ...Array.from(answerErrors, ([rowId, message]) => ({ key: `answer-${rowId}`, message })),
  ].filter((e): e is { key: string; message: string } => Boolean(e));
  const filterLabel = FILTER_OPTIONS.find((o) => o.value === sourceFilter)?.label ?? 'Everything';

  return (
    <div className="today contents">
      <div className="flex flex-1 flex-col gap-6 min-w-0 min-h-0 overflow-y-auto today-page">
        <div className="today-top">
          <div className="flex flex-1 flex-col gap-1 min-w-0 today-greeting">
            <h1>{board.heading}</h1>
            <p className="m-0 text-lg text-muted">{board.lead}</p>
          </div>
          <Button asChild kind="primary" className="flex-none">
            <a
              href={pathForMailCompose(agoraId)}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                event.preventDefault();
                go(pathForMailCompose(agoraId));
              }}
            >
              <Icon name="pen" size={18} />
              <span>Write</span>
            </a>
          </Button>
        </div>
        <div className="today-grid">
          <section className="card flex flex-col overflow-hidden today-waiting">
            <div className="today-waiting-head">
              <h2 className="h3 grow min-w-0">Waiting for you</h2>
              {loaded && <span className="chip c-out">{filteredRows.length}</span>}
              <Select
                icon={<Icon name="filter" size={16} />}
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value as SourceFilter)}
                aria-label="Filter by source"
              >
                {FILTER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </div>
            {errors.map(({ key, message }) => (
              <p key={key} role="alert" className="text-xs text-bad pt-3 px-5 pb-0">
                {message}
              </p>
            ))}
            {!loaded && <p className="text-sm text-muted p-5">Loading…</p>}
            {filteredRows.map((row) => (
              <WaitingRowLink
                key={row.id}
                row={row}
                go={go}
                busy={answering.has(row.id)}
                onAnswer={(status) => answer(row, status)}
              />
            ))}
            {loaded && rows.length === 0 && errors.length === 0 && (
              <p className="text-sm text-muted p-5">Nothing is waiting for you right now.</p>
            )}
            {loaded && rows.length > 0 && filteredRows.length === 0 && (
              <p className="text-sm text-muted p-5">
                Nothing here for {filterLabel.toLowerCase()}.
              </p>
            )}
            {loaded && (
              <a
                className="lnk text-sm today-all"
                href={pathForMail(agoraId)}
                onClick={(event) => {
                  event.preventDefault();
                  go(pathForMail(agoraId));
                }}
              >
                Everything from the last week
              </a>
            )}
          </section>
          <div className="flex flex-col gap-6 min-w-0">
            <section className="card pad today-agenda">
              <div className="today-agenda-head">
                <h2 className="h3 grow min-w-0">Your day</h2>
                <a
                  className="lnk text-sm"
                  href={pathForCalendar(agoraId)}
                  onClick={(event) => {
                    event.preventDefault();
                    go(pathForCalendar(agoraId));
                  }}
                >
                  Open Calendar
                </a>
              </div>
              {calendarClient ? (
                <AgendaList rows={items} go={go} canJoin={callsEnabled} />
              ) : (
                <Empty icon="calendar">Nothing on your calendar today.</Empty>
              )}
            </section>
            {around.length > 0 && (
              <section className="card pad today-around" aria-labelledby="today-around-h">
                <h2 id="today-around-h" className="h3">
                  Around right now
                </h2>
                <ul className="today-around-list">
                  {around.map((person) => (
                    <li key={person.userId}>
                      <a
                        href={person.path}
                        className="today-around-row"
                        onClick={(event) => {
                          if (
                            event.metaKey ||
                            event.ctrlKey ||
                            event.shiftKey ||
                            event.button !== 0
                          )
                            return;
                          event.preventDefault();
                          go(person.path);
                        }}
                      >
                        <Avatar name={person.name} seed={person.userId} size="sm" />
                        <span className="text-sm font-strong truncate grow min-w-0">
                          {person.name}
                        </span>
                        <span className="text-xs text-muted">{AROUND_LABEL[person.state]}</span>
                        <Presence state={presenceState(person.state) ?? 'on'} size="sm" />
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const AROUND_LABEL: Record<AroundPerson['state'], string> = {
  online: 'Online',
  busy: 'Busy',
  away: 'Away',
};

function WaitingRowLink({
  row,
  go,
  busy,
  onAnswer,
}: {
  row: WaitingRow;
  go: (path: string) => void;
  busy: boolean;
  onAnswer: (status: 'accepted' | 'declined') => void;
}) {
  const open = (event: React.MouseEvent) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    go(row.action.path);
  };
  const body = (
    <>
      <span className="today-row-mark" style={stickerOf(SOURCE_HUE[row.source])} aria-hidden="true">
        <Icon name={row.icon} size={16} />
      </span>
      <span className="flex flex-col grow min-w-0 gap-0.75">
        <span className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-strong truncate">{row.who}</span>
          {row.invite && <span className="chip c-out flex-none">Needs an answer</span>}
        </span>
        <span className="text-sm truncate">{row.what}</span>
        <span className="text-xs text-muted truncate">{row.detail}</span>
      </span>
      <span className="today-row-when text-xs text-muted">{whenLabel(row)}</span>
    </>
  );
  if (row.invite) {
    const { title } = row.invite;
    return (
      <div className="today-row">
        <a href={row.action.path} className="today-row-main" onClick={open}>
          {body}
        </a>
        <span className="today-row-action today-row-answer">
          <Button
            size="sm"
            kind="primary"
            busy={busy}
            aria-label={`Say yes to ${title}`}
            onPress={() => onAnswer('accepted')}
          >
            Yes
          </Button>
          <Button
            size="sm"
            busy={busy}
            aria-label={`Say no to ${title}`}
            onPress={() => onAnswer('declined')}
          >
            No
          </Button>
        </span>
      </div>
    );
  }
  return (
    <a href={row.action.path} className="today-row" onClick={open}>
      {body}
      <span className="btn size-sm today-row-action pointer-events-none" aria-hidden="true">
        <Icon name={actionIcon(row.action.label)} size={16} />
        <span>{row.action.label}</span>
      </span>
    </a>
  );
}

/** "Your day": a time, the event, where, and the link that opens it. */
function AgendaList({
  rows,
  go,
  canJoin,
}: {
  rows: readonly AgendaRow[];
  go: (path: string) => void;
  canJoin: boolean;
}) {
  if (rows.length === 0) return <Empty icon="calendar">Nothing on your calendar today.</Empty>;
  return (
    <ol aria-label="Today's agenda" className="today-agenda-list">
      {rows.map((row) => (
        <li
          key={row.id}
          className={row.ended ? 'today-agenda-item today-agenda-ended' : 'today-agenda-item'}
        >
          <a
            href={row.path}
            className="today-agenda-row"
            onClick={(event) => {
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
              event.preventDefault();
              go(row.path);
            }}
          >
            <time dateTime={row.at.toISOString()} className="today-agenda-time text-xs text-muted">
              {row.at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </time>
            <span
              className="today-agenda-body"
              style={row.color ? ({ '--ev-color': row.color } as React.CSSProperties) : undefined}
            >
              <span className="flex items-center gap-2">
                <span className="text-sm font-strong truncate">{row.title}</span>
                {row.now && <span className="chip">Now</span>}
              </span>
              {row.where && <span className="text-xs text-muted truncate">{row.where}</span>}
              {row.people.length > 0 && (
                <span className="text-xs text-muted truncate">{peopleLine(row.people)}</span>
              )}
            </span>
          </a>
          {canJoin && row.joinPath && (
            <Button
              size="sm"
              kind="primary"
              className="flex-none"
              aria-label={`Join ${row.title}`}
              onPress={() => go(row.joinPath as string)}
            >
              Join
            </Button>
          )}
        </li>
      ))}
    </ol>
  );
}
